import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Grid3X3, Loader2 } from 'lucide-react';
import ContestCardWithCountdown from '../components/contest/ContestCardWithCountdown';
import FooterAdvert, { FOOTER_ADVERT_RESERVED_SPACE } from '../components/adverts/FooterAdvert';
import { CARD_LIST_GRID } from '../utils/desktopColumn';
import SquareResultsModal from '../components/modals/SquareResultsModal';
import { useGetMyPicksQuery } from '../api/squares/getMyPicks';
import { useGetFixtureScoresBatchQuery } from '../api/squares/getFixtureScores';
import { formatTimeRemaining } from '../lib/dateFormat';
import { usePaginatedScroll } from '@low6-pickem/ui';
import { appendLobbyParams } from '@/utils/lobbyNavigation';

/**
 * My Picks View
 * Shows the user's active picks/entries
 */
export default function MyPicksView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedContestId, setSelectedContestId] = useState<string | null>(null);

  // Fetch user's squares contests
  const { data: myPicks = [], isLoading, error } = useGetMyPicksQuery();

  const contestIdsNeedingScores = useMemo(() => {
    return myPicks
      .map((pick: any) => (pick.question_id || pick.squares_contest_id) as string | undefined)
      .filter(Boolean) as string[];
  }, [myPicks]);

  const { data: fixtureScoresBatch } = useGetFixtureScoresBatchQuery(contestIdsNeedingScores);

  // Transform API data to ContestCard format
  const transformedPicks = useMemo(() => {
    const fixtures = fixtureScoresBatch?.fixtures || {};
    return myPicks.map((pick: any) => {
      const startTime = pick.start_time_utc ? new Date(pick.start_time_utc) : null;
      // Support both Pickem statuses (ACTIVE, COMPLETE) and legacy statuses (live, final)
      // ACTIVE = entries locked → show View Results CTA (not Edit Entry)
      const pickStatus = pick.status?.toUpperCase();
      const isLive = pickStatus === 'ACTIVE' || pick.status === 'live' || pick.status === 'final';
      const isCompleted = pickStatus === 'COMPLETE' || pick.status === 'final';

      // Format start time using formatTimeRemaining (will be updated live by ContestCardWithCountdown)
      const startTimeDisplay = startTime ? formatTimeRemaining(startTime) : 'TBD';

      // Format prize pool
      const prizePool = `$${pick.total_prize_amount?.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '0.00'}`;

      const contestId = pick.question_id || pick.squares_contest_id;
      const fixture = contestId ? fixtures[String(contestId)] : null;

      // Derive period label from fixture (Q1/H1, Q2/H2, etc.) and clock time
      let period: string | undefined;
      let timeRemaining: string | undefined;
      if (fixture?.status === 'live' || fixture?.status === 'finished') {
        const feedQuarter = fixture.quarter != null ? Number(fixture.quarter) : null;
        const sport = (pick.sport || '').toUpperCase();
        const isFootball = sport === 'NFL' || sport === 'NCAAMB' || sport === 'NCAA';
        if (feedQuarter != null && feedQuarter >= 1 && feedQuarter <= 4) {
          period = isFootball && feedQuarter <= 2 ? `H${feedQuarter}` : `Q${feedQuarter}`;
        } else if (fixture?.clock) {
          const m = fixture.clock.match(/Q([1-4])|H([12])/i);
          if (m) period = m[1] ? `Q${m[1]}` : `H${m[2]}`;
        }
        const timeMatch = fixture?.clock?.match(/(\d+:\d+)/);
        timeRemaining = fixture?.status === 'live' && timeMatch ? timeMatch[1] : undefined;
        if (fixture?.status === 'finished' && !period) period = 'FT';
      }

      const rowTeamId = pick.row_team_id as string | undefined;
      const colTeamId = pick.col_team_id as string | undefined;
      const homeId = fixture?.homeTeam?.id;
      const awayId = fixture?.awayTeam?.id;
      const homeScore = fixture?.homeScore ?? 0;
      const awayScore = fixture?.awayScore ?? 0;
      const rowScore =
        rowTeamId && homeId && rowTeamId === homeId
          ? homeScore
          : rowTeamId && awayId && rowTeamId === awayId
            ? awayScore
            : homeScore;
      const colScore =
        colTeamId && awayId && colTeamId === awayId
          ? awayScore
          : colTeamId && homeId && colTeamId === homeId
            ? homeScore
            : awayScore;

      return {
        id: contestId,
        title: pick.contest_name_en || pick.contest_name_fr || 'Squares Contest',
        startTime: startTimeDisplay,
        startDateTime: pick.start_time_utc,
        gameCount: 1,
        entries: pick.total_entries_to_date || 0,
        entryPrice: 'FREE',
        prizePool,
        league: pick.sport as 'NFL' | 'NBA' | 'NHL' | 'NCAAMB',
        sport: pick.sport,
        status: isLive ? 'live' : isCompleted ? 'completed' : 'upcoming',
        isLive,
        isGameFinal: isCompleted,
        liveData:
          isLive || isCompleted
            ? {
                team1Name: pick.row_team_display_name || 'Team 1',
                team1Score: rowScore,
                team2Name: pick.col_team_display_name || 'Team 2',
                team2Score: colScore,
                totalWinnings: `$${(pick.total_winnings ?? 0).toFixed(2)}`,
                period,
                timeRemaining,
              }
            : undefined,
        hasWinnings: (pick.total_winnings ?? 0) > 0,
        // Reserve a Seat (DKPLAT-529) — reserved-but-not-yet-picked card state
        hasReservation: !!pick.has_reservation,
        // DKPLAT-533: on a reserved card these must fail CLOSED. Defaulting them to
        // `true` when the field is absent stacked a second fail-open on top of the
        // backend's, which is what made a reserved contest with no posted-at time
        // render "No Picks Submitted - Make Selections Now!" with an enabled Edit.
        // Non-reserved cards keep the permissive default so legacy contests (which
        // predate these fields) are unaffected.
        hasSelections:
          pick.has_selections !== undefined ? !!pick.has_selections : !pick.has_reservation,
        // Live "squares now open" signal — flips the reserved card from
        // "Entry Reserved" to "No Picks Submitted - Make Selections Now!".
        hasQuestions:
          pick.has_questions !== undefined ? !!pick.has_questions : !pick.has_reservation,
        questionsPostedAt: pick.questions_posted_at ?? null,
        adSlot: pick.ad_units?.length
          ? {
              enabled: true,
              slotId: `contest-${pick.squares_contest_id}-banner`,
            }
          : undefined,
      };
    });
  }, [myPicks, fixtureScoresBatch?.fixtures]);

  const handleContestClick = (contestId: string) => {
    // Navigate to PlayView - when entries locked (ACTIVE/COMPLETE), show results mode with View Results CTA
    const contest = myPicks.find((p: any) => (p.question_id || p.squares_contest_id) === contestId);
    const contestStatus = contest?.status?.toUpperCase();
    const isLegacyLive = contest?.status === 'live' || contest?.status === 'final';
    const entriesLocked =
      contestStatus === 'ACTIVE' || contestStatus === 'COMPLETE' || isLegacyLive;

    if (entriesLocked) {
      navigate(appendLobbyParams(`/play?contestId=${contestId}&mode=results`, searchParams));
    } else {
      navigate(appendLobbyParams(`/play?contestId=${contestId}`, searchParams));
    }
  };

  const handleViewResults = (contestId: string) => {
    // Navigate directly to PlayView in results mode
    navigate(appendLobbyParams(`/play?contestId=${contestId}&mode=results`, searchParams));
  };

  const handleMenuClick = (contestId: string) => {
    // Navigate to rules/help view for this contest
    navigate(appendLobbyParams(`/help?contestId=${contestId}`, searchParams));
  };

  const handleCloseModal = () => {
    setShowResultsModal(false);
    setSelectedContestId(null);
  };

  // Filter out completed contests - they should only show in Recent (ResultsView)
  const activePicks = useMemo(() => {
    return transformedPicks.filter((pick: any) => {
      // Find the original pick data to check status
      const originalPick = myPicks.find(
        (p: any) => (p.question_id || p.squares_contest_id) === pick.id
      );
      if (!originalPick) return true; // Keep if we can't find original (shouldn't happen)

      // Check if contest is completed
      const statusUpper = originalPick.status?.toUpperCase?.() ?? '';
      const isCompleted =
        statusUpper === 'COMPLETE' ||
        originalPick.status === 'final' ||
        originalPick.status === 'completed';

      // Exclude completed contests from My Picks
      return !isCompleted;
    });
  }, [transformedPicks, myPicks]);

  // Get selected contest data for modal
  const selectedContest = activePicks.find((pick: any) => pick.id === selectedContestId);

  // Order: live first, then non-live (for ad insertion between cards)
  const orderedPicks = useMemo(
    () => [
      ...activePicks.filter((p: any) => p.isLive),
      ...activePicks.filter((p: any) => !p.isLive),
    ],
    [activePicks]
  );

  const {
    visibleItems: visiblePicks,
    sentinelRef,
    hasMore,
    previousCount,
  } = usePaginatedScroll(orderedPicks);

  // Loading state
  if (isLoading) {
    return (
      <div className="px-4 py-8 flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#53d337] mb-4" />
        <p className="text-zinc-400">Loading your picks...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="px-4 py-8 flex flex-col items-center justify-center min-h-[400px]">
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4">
          <p className="text-red-400 font-semibold">Error loading picks</p>
          <p className="text-red-300 text-sm mt-1">
            {error instanceof Error ? error.message : 'Failed to load your picks'}
          </p>
        </div>
      </div>
    );
  }

  // Empty state
  if (activePicks.length === 0) {
    return (
      <div className="px-4 py-8 flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-20 h-20 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
          <Grid3X3 className="w-10 h-10 text-zinc-600" />
        </div>
        <h2 className="text-white font-semibold text-lg mb-2">No Picks Yet</h2>
        <p className="text-zinc-500 text-sm text-center max-w-xs">
          You haven't entered any contests yet. Browse available contests and pick your squares!
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 py-4" style={{ paddingBottom: FOOTER_ADVERT_RESERVED_SPACE }}>
      {/* User's Contest Entries (live first, then non-live); 6 at a time, load more on scroll */}
      <div className={CARD_LIST_GRID}>
        {visiblePicks.map((pick: any, index: number) => {
          // Apply fade-in animation to newly loaded items
          const isNewlyLoaded = index >= previousCount && previousCount > 0;

          return (
            <div key={pick.id} className={isNewlyLoaded ? 'contest-card-fade-in' : ''}>
              <ContestCardWithCountdown
                id={pick.id}
                title={pick.title}
                startTime={pick.startTime}
                startDateTime={pick.startDateTime}
                gameCount={pick.gameCount}
                entries={pick.entries}
                entryPrice={pick.entryPrice}
                prizePool={pick.prizePool}
                league={pick.league}
                sport={pick.sport}
                status={pick.status}
                isLive={pick.isLive}
                isGameFinal={pick.isGameFinal}
                liveData={pick.liveData}
                adSlot={pick.adSlot}
                hasReservation={pick.hasReservation}
                hasSelections={pick.hasSelections}
                hasQuestions={pick.hasQuestions}
                questionsPostedAt={pick.questionsPostedAt}
                onClick={() => handleContestClick(pick.id)}
                onMenuClick={() => handleMenuClick(pick.id)}
                onViewResults={pick.isLive ? () => handleViewResults(pick.id) : undefined}
                onViewGame={pick.isGameFinal ? () => handleContestClick(pick.id) : undefined}
                // State 2 (reserved + squares now open): the enabled reserved-row Edit
                // button drives the picks flow. Only wired for the reserved-unconverted
                // case so the normal entered-contest whole-card-click UX is unchanged.
                onEditPick={
                  pick.hasReservation && !pick.hasSelections
                    ? () => handleContestClick(pick.id)
                    : undefined
                }
              />
            </div>
          );
        })}
        {hasMore && <div ref={sentinelRef} className="h-4" aria-hidden />}
      </div>

      {/* Footer Advert */}
      <FooterAdvert />

      {/* Square Results Modal */}
      {selectedContest && (
        <SquareResultsModal
          contestId={selectedContestId}
          isOpen={showResultsModal}
          onClose={handleCloseModal}
          team1Name={selectedContest.liveData?.team1Name}
          team2Name={selectedContest.liveData?.team2Name}
          team1Score={selectedContest.liveData?.team1Score}
          team2Score={selectedContest.liveData?.team2Score}
          sport={selectedContest.sport}
        />
      )}
    </div>
  );
}
